import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! How can I help you today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);

  const quickQuestions = [
    "How do I install the EDR agent?",
    "What are your pricing plans?",
    "How do I connect my email?",
    "How do I reset my password?",
  ];

  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const maxChars = 500;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (messageText = null) => {
    const textToSend = messageText || inputValue;
    if (!textToSend.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: textToSend,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Please log in to use the chat feature');
      }

      const response = await fetch('https://cyberguard-backend-wa00.onrender.com/api/chat/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: textToSend,
          analytics: {
            session_id: sessionStorage.getItem('chat_session_id') || Date.now().toString(),
            client_time: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        if (response.status === 401) {
          throw new Error('Your session has expired. Please log in again.');
        }
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();

      if (!sessionStorage.getItem('chat_session_id')) {
        sessionStorage.setItem('chat_session_id', Date.now().toString());
      }

      const botMessage = {
        id: Date.now() + 1,
        text: data.response || data.message || 'No response received',
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: error.message || "I'm having trouble connecting. Please try again.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // Floating button when closed
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-5 right-5 bg-cyan-600 hover:bg-cyan-700 text-white p-4 rounded-full shadow-lg transition-all duration-200 z-50"
        aria-label="Open chat"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 right-0 md:bottom-5 md:right-5 bg-white md:rounded-xl shadow-xl z-50 w-full md:w-96 h-[100vh] md:h-[520px] flex flex-col overflow-hidden border border-gray-200">

      {/* Header - Clean and simple */}
      <div className="bg-cyan-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
            <span className="text-lg">🛡️</span>
          </div>
          <div>
            <h3 className="font-semibold text-sm">CyberGuard Support</h3>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
              <span className="text-xs text-cyan-100">Online</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="hover:bg-white/20 p-1.5 rounded-lg transition-colors"
          aria-label="Close chat"
        >
          <X size={20} />
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex flex-col ${message.sender === 'user' ? 'items-end' : 'items-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                message.sender === 'user'
                  ? 'bg-cyan-600 text-white rounded-br-md'
                  : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.text}</p>
            </div>
            <span className="text-xs text-gray-400 mt-1 px-1">
              {formatTime(message.timestamp)}
            </span>
          </div>
        ))}

        {isTyping && (
          <div className="flex items-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex space-x-1.5">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Questions - Simple chips */}
      {messages.length <= 2 && (
        <div className="px-4 py-2 bg-white border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {quickQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => sendMessage(question)}
                className="text-xs bg-gray-100 hover:bg-cyan-50 hover:text-cyan-700 text-gray-600 px-3 py-1.5 rounded-full transition-colors border border-gray-200 hover:border-cyan-200"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area - Clean design */}
      <div className="p-3 bg-white border-t border-gray-200">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value.slice(0, maxChars))}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="w-full resize-none border border-gray-300 focus:border-cyan-500 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-cyan-500/30 text-sm"
              rows="1"
              style={{ minHeight: '42px', maxHeight: '100px' }}
            />
          </div>
          <button
            onClick={() => sendMessage()}
            disabled={!inputValue.trim() || isTyping}
            className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-2.5 rounded-xl transition-colors"
            aria-label="Send message"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        <div className="flex justify-end mt-1">
          <span className="text-xs text-gray-400">
            {maxChars - inputValue.length} characters left
          </span>
        </div>
      </div>
    </div>
  );
};

export default ChatWidget;
